// Shared constants. These used to live as copy-pasted copies in
// Dashboard.jsx / Booking.jsx / Detailer.jsx — a known drift hazard
// flagged in CLAUDE.md. Keep this file as the single source.

// Job length in hours. Every WASH-BAY service is a 3-hour appointment (set
// 2026-08-31) so it fits exactly one of the three daily start times below.
// Clean-bay coating / correction work keeps its real multi-day length — those
// are not appointments; they occupy the bay for days.
export const SERVICE_DURATIONS = {
  'Interior Detail': 3,
  'Exterior Detail': 3,
  'Signature Detail': 3,
  'Paint Correction': 72,
  'Ceramic Coating': 168,
  'Avant Guard Formula 1051': 168,
  'Extraction': 3,
  'Extraction +': 3,
  'Ex. & Wipe': 3,          // legacy name, kept so old bookings still resolve
  'Trim & Plastic Restoration': 3,
  '2-Step Paint Correction': 48,
  'Wheel Coating': 3,
  'Glass Coating': 3,
  'Headlight Restoration': 2,
  'Strip and Shine - Ground': 3,
  'Strip and Shine - Lift': 3,
  'Strip and Shine - Wheels Off': 3,
  "Gentleman's Spec": 3,
  // Canonical Seasonal Ceramic Coating label — picker shows only this
  // (S.C.C.) variant. The legacy plain-name + S.C.C.-alone keys were
  // removed from this duration map on 2026-06-09 (UI dedup). Existing
  // bookings stored under those legacy names still resolve via
  // SERVICE_PRICES / SIZED_SERVICE_PRICES (kept as backwards-compat).
  'Seasonal Ceramic Coat (S.C.C.)': 48,
  // Specialty Service — generic catch-all, blank checklist template
  // (gas/Checklists.js _resolveSvcTasks has no match → empty task list,
  // user fills in their own via the edit form's Specialty Service
  // section or the inline + Add custom task input).
  'Specialty Service': 3,
  // Name variants used by the customer booking page / invoicer catalogs, so
  // duration resolves whichever label a booking stored.
  'Gentleman Spec': 3,

  // ── Winter Menu (season 2026-27) ──────────────────────────────────────
  // Hours, like everything above. The two Salt-Off figures are the ones the
  // flier states (~45 min / ~90 min); the other two are not stated anywhere,
  // so they take the standard 3h slot rather than a number nobody decided.
  //
  // ⚠️ A 45-minute wash still consumes one of the day's THREE booking slots
  // (BOOKING_SLOTS 9/12/3, BOOKING_DAY_CAP 3). A winter book that runs every
  // 2-3 weeks per customer hits that cap long before it fills the bay — the
  // capacity model is a separate decision from the duration.
  'Winter Exterior + Iron Decon': 3,
  'Salt-Stain Reset': 3,
  'Salt-Off Express': 0.75,
  'Salt-Off Plus': 1.5,

  // ── Parent services (2026-09-26) ──────────────────────────────────────
  // Standard 3h slot for both until the shop times a real one.
  'Backseat Overhaul': 3,
  'New Parent Prep': 3,
};

export const STATUS_LABELS = {
  scheduled: 'Scheduled',
  checked_in: 'Checked In',
  in_progress: 'In Progress',
  complete: 'Complete',
  cancelled: 'Cancelled',
  delivered: 'Delivered',
};

export const BAYS = { WASH: 'wash_bay', CLEAN: 'clean_bay' };

// ── Service categories ─────────────────────────────────────────────────────
// WHAT KIND of work a job is, which is not the same question as WHERE the car
// sits. It used to be: two bays, two kinds of work, one column doing both jobs.
//
// Mechanical work split them apart. A mechanical job runs in whichever bay is
// free — the operator picks — so a mechanical job in the wash bay and a general
// detail in the wash bay have the same `bay` and are completely different jobs.
// Hence a separate `service_category` column (migration 20260914120000) and
// this map, with `bay` returned to meaning only "which bay".
//
// 🚨 `dot` is the colour the SCHEDULE paints for this category, and it is the
// shop's own colour language — red = general, green = specialty, yellow =
// mechanical. Don't re-map these to the app's blue/teal accent tokens: staff
// read the calendar by these three colours.
//
// `bay` here is the DEFAULT bay for the category. `baySelectable` says whether
// the operator may override it — true only for mechanical, because general and
// specialty work are defined by the bay they run in.
export const SERVICE_CATEGORIES = {
  general:    { key:'general',    label:'General Detailing',   short:'General',    dot:'#ef4444', bay:'wash_bay',  baySelectable:false },
  specialty:  { key:'specialty',  label:'Specialty Detailing', short:'Specialty',  dot:'#10b981', bay:'clean_bay', baySelectable:false },
  mechanical: { key:'mechanical', label:'Mechanical',          short:'Mechanical', dot:'#eab308', bay:'wash_bay',  baySelectable:true  },
};
// Display order — the booking page's category ticker and the schedule legend
// both read this, so the three never appear in two different orders.
export const SERVICE_CATEGORY_ORDER = ['general','specialty','mechanical'];

// ── Mechanical menu (Ryder, 2026-10-06) ───────────────────────────────────
// Mechanical is priced as LABOR: hours x SHOP_LABOR_RATE. The ranges are the
// mechanic's own estimates; a booking defaults to the TOP of the range (Ryder's
// pick) so the bay is never double-booked, and staff trim the hours on the
// booking when the job is smaller. Diagnostic has no top ("X amount of time"),
// so it books at its 0.5h starting point and is extended when it turns into
// real digging.
//
// `returns` is the mechanic's note that a "proper" service takes more than one
// visit — it becomes a checklist task so the follow-up actually gets booked.
//
// The free-text "+ Mechanical Service" entry stays for anything off-menu.
export const SHOP_LABOR_RATE = 115;
export const MECH_MENU = {
  'Oil + Filter Service':        { min: 0.25, max: 2.5 },
  'Transmission Service':        { min: 2,    max: 6,  note: 'Includes test drive. Filter is optional, per customer request.', returns: 3 },
  'Engine / Cabin Air Filter':   { min: 0.5,  max: 1.5 },
  'Tire / Wheel Service':        { min: 0.5,  max: 4 },
  'Brake / Suspension Service':  { min: 2,    max: 8,  returns: 2 },
  'General Repair':              { min: 1,    max: 15 },
  'General Diagnostic':          { min: 0.5,  max: 0.5, note: 'Starts at 0.5h; extend for serious diagnostic work.' },
};
export const MECH_SERVICES = Object.keys(MECH_MENU);
// Durations + prices are filled in from MECH_MENU right after SERVICE_PRICES
// is declared (further down), so the menu is the only place a number lives.
export function isMechService(name) {
  return Object.prototype.hasOwnProperty.call(MECH_MENU, String(name || '').trim());
}

// The category of an EXISTING booking row. Prefers the stored value; falls back
// to what `bay` used to mean for rows written before service_category existed
// (and for any writer that doesn't send it yet, e.g. recurring bookings).
//
// Never returns null — an unrecognised value falls back too, because a booking
// that maps to no category paints no dot, which on the calendar reads as a
// booking that isn't there at all.
export function categoryForBooking(b) {
  const c = String((b && (b.serviceCategory || b.service_category)) || '').toLowerCase().trim();
  if (SERVICE_CATEGORIES[c]) return c;
  return String((b && b.bay) || '').toLowerCase().trim() === 'clean_bay' ? 'specialty' : 'general';
}

// The schedule dot colour for a booking, via the same fallback.
export function categoryDot(b) {
  return SERVICE_CATEGORIES[categoryForBooking(b)].dot;
}

// ── Daily capacity: three details a day, three hours apart ─────────────────
// The shop's rule, and now ONE definition of it. The customer booking pages
// have enforced this since /api/booking-availability replaced the Apps Script
// version; the staff Booking page offered eight hourly buttons and no daily
// limit at all, so the two calendars disagreed about what a full day was.
//
// 🚨 These are imported by `api/booking-availability.js`, a serverless function
// — so this file must stay a PURE module (no browser globals, no React), same
// rule as lib/waivers.js. Adding a `window`/`document` reference anywhere in
// here breaks the public booking pages, not just the app.
//
// The AMOUNT of time a job takes is NOT set here: SERVICE_DURATIONS above still
// owns that. Every wash-bay service is 3h now, so a detail starting at 9:00
// hands the bay over at 12:00 and the three slots tile the day exactly.
// These are the times a job may START.
export const BOOKING_SLOTS = ['09:00', '12:00', '15:00'];
export const BOOKING_DAY_CAP = 3;
// No per-service exception. A Gentleman's Spec used to drop the day to two
// because it ran five hours; it is a 3-hour appointment now, so it fills one
// slot like everything else, and every day takes three (2026-08-31).
// A booking that no longer occupies its slot.
export const DEAD_BOOKING_STATUSES = ['cancelled', 'deleted'];

// Vehicle size tiers — match the sized-service price tiers in
// public/3c-booking.html and public/3c-invoicer.html CATALOGs. Captured
// on the booking row at booking time and threaded into the invoicer
// deeplink so the size dropdown lands pre-selected for that booking's
// vehicle, dropping a click off every sized line item.
export const VEHICLE_SIZES = [
  'Coupe',
  'Sedan',
  'Hatchback & Midsize SUV',
  'Full Size SUV & Half Ton Truck',
  'One Ton Truck & Vans',
];

// Service pricing — projected-revenue calc. Mirrors public/3c-booking.html
// CATALOG so projections match what the customer-facing form quotes.
// Customer bookings come in as `<prefix> - <size>` (e.g. "Pro - Coupe");
// staff bookings come in as the bare service name (e.g. "Pro Service")
// with no vehicle size. Sedan is used as the projection default for
// staff bookings since it's the modal vehicle class.
export const SIZED_SERVICE_PRICES = {
  // "Int. or Ext." is the customer-page combined option; at approval staff
  // split it into "Interior" / "Exterior" (same price tier) — all three keys
  // point at the same prices so whichever label a booking stored prices right.
  'Int. or Ext.':       {'Coupe':260, 'Sedan':305, 'Hatchback & Midsize SUV':350, 'Full Size SUV & Half Ton Truck':395, 'One Ton Truck & Vans':440},
  'Interior':           {'Coupe':260, 'Sedan':305, 'Hatchback & Midsize SUV':350, 'Full Size SUV & Half Ton Truck':395, 'One Ton Truck & Vans':440},
  'Exterior':           {'Coupe':260, 'Sedan':305, 'Hatchback & Midsize SUV':350, 'Full Size SUV & Half Ton Truck':395, 'One Ton Truck & Vans':440},
  'Signature':          {'Coupe':380, 'Sedan':425, 'Hatchback & Midsize SUV':470, 'Full Size SUV & Half Ton Truck':515, 'One Ton Truck & Vans':560},
  // Monthly membership dues by vehicle tier — the shop's Stripe price sheet
  // (shop.html MEMBERSHIP_PRICES: sedan/mid/full/oneton). Coupe = Sedan; the
  // shop has no Coupe tier. Mirrors the invoicer's Memberships category.
  'Bronze Membership':  {'Coupe':185, 'Sedan':185, 'Hatchback & Midsize SUV':205, 'Full Size SUV & Half Ton Truck':225, 'One Ton Truck & Vans':245},
  'Silver Membership':  {'Coupe':255, 'Sedan':255, 'Hatchback & Midsize SUV':275, 'Full Size SUV & Half Ton Truck':295, 'One Ton Truck & Vans':315},
  'Gold Membership':    {'Coupe':345, 'Sedan':345, 'Hatchback & Midsize SUV':365, 'Full Size SUV & Half Ton Truck':385, 'One Ton Truck & Vans':405},
  // Historical bookings created before the 2026-05-25 rename will have
  // service='Advanced' — keep the old key aliased to the same prices so
  // revenue projections keep matching them.
  'Advanced':           {'Coupe':380, 'Sedan':425, 'Hatchback & Midsize SUV':470, 'Full Size SUV & Half Ton Truck':515, 'One Ton Truck & Vans':560},
  // Pro Service was retired 2026-05-25 and folded into Signature.
  // Legacy bookings written before that retirement still have
  // service='Pro - <size>' or service='Pro Service'; alias them to the
  // same Signature price tier so projections keep counting them.
  'Pro':                {'Coupe':380, 'Sedan':425, 'Hatchback & Midsize SUV':470, 'Full Size SUV & Half Ton Truck':515, 'One Ton Truck & Vans':560},
  "Gentleman's Spec":   {'Coupe':480, 'Sedan':525, 'Hatchback & Midsize SUV':570, 'Full Size SUV & Half Ton Truck':615, 'One Ton Truck & Vans':660},
  // 1Y.1S retired 2026-06-11 (no longer offered — folded into Seasonal
  // Ceramic Coating). Kept as a legacy price alias ONLY so historical
  // 1Y.1S bookings/invoices still value correctly in revenue/3CAM.
  '1Y.1S':              {'Coupe':1100,'Sedan':1200,'Hatchback & Midsize SUV':1300,'Full Size SUV & Half Ton Truck':1400,'One Ton Truck & Vans':1500},
  '5Y.2S':              {'Coupe':1500,'Sedan':1700,'Hatchback & Midsize SUV':1900,'Full Size SUV & Half Ton Truck':2100,'One Ton Truck & Vans':2300},
  'AG 1051':            {'Coupe':2500,'Sedan':2700,'Hatchback & Midsize SUV':2900,'Full Size SUV & Half Ton Truck':3100,'One Ton Truck & Vans':3300},
  // Seasonal Ceramic Coating (2026-06-11: +$50 across the board; now a
  // 1-step polish + 1-year ceramic coating). Canonical name + the legacy
  // 'S.C.C.' alias both point at the same current tier.
  'Seasonal Ceramic Coating': {'Coupe':900, 'Sedan':950, 'Hatchback & Midsize SUV':1000, 'Full Size SUV & Half Ton Truck':1050, 'One Ton Truck & Vans':1150},
  'S.C.C.':             {'Coupe':900, 'Sedan':950, 'Hatchback & Midsize SUV':1000, 'Full Size SUV & Half Ton Truck':1050, 'One Ton Truck & Vans':1150},
  // Parent services (Ryder, 2026-09-26). Coupe = Sedan, as with memberships.
  // Backseat Overhaul is a MONTHLY subscription (Stripe, see BACKSEAT_OVERHAUL
  // in api/invoicer.js — keep the two in step); the visit itself invoices at $0
  // as a membership redeem. New Parent Prep is a one-time service.
  'Backseat Overhaul':  {'Coupe':125, 'Sedan':125, 'Hatchback & Midsize SUV':145, 'Full Size SUV & Half Ton Truck':165, 'One Ton Truck & Vans':185},
  'New Parent Prep':    {'Coupe':395, 'Sedan':395, 'Hatchback & Midsize SUV':440, 'Full Size SUV & Half Ton Truck':485, 'One Ton Truck & Vans':530},
};
// Bare-name lookups for staff bookings (Booking.jsx WASH/CLEAN_SERVICES)
// and unsized catalog items.
export const SERVICE_PRICES = {
  // Membership tiers at the Sedan price — the shop's Stripe sheet (Silver was
  // corrected 225→255 on 2026-07-11 when the shop moved to Stripe; the bare
  // alias lagged). Sized lines ("Bronze Membership – Sedan") price off
  // SIZED_SERVICE_PRICES below; these catch the bare name. The invoicer's
  // Memberships category and shop.html MEMBERSHIP_PRICES must agree with both.
  'Bronze Membership': 185,
  'Silver Membership': 255,
  'Gold Membership':   345,
  // Twelve months paid once — flat, any vehicle (2026-09-23).
  '12-Month Package – Bronze': 2100,
  '12-Month Package – Silver': 2800,
  '12-Month Package – Gold':   3800,
  // Parent services — Sedan price; sized lines price off SIZED_SERVICE_PRICES.
  'Backseat Overhaul': 125,
  'New Parent Prep': 395,
  // Wash services — Sedan default for sized ones
  'Interior Detail': 305,
  'Exterior Detail': 305,
  'Signature Detail': 425,
  // Legacy alias — bookings before the 2026-05-25 rename used this key.
  'Advanced Detail': 425,
  // Pro Service was folded into Signature on 2026-05-25 — alias the
  // bare name + the staff-side "Pro Service" / "Pro" variants to the
  // same Sedan price so legacy bookings project consistently.
  'Pro Service': 425,
  'Pro': 425,
  'Trim & Plastic Restoration': 175,
  'Extraction': 200,
  'Extraction +': 375,
  'Extraction': 200,
  'Ex. & Wipe': 375,       // legacy name — see the rename note in checklistTemplates.js
  // Clean services — Sedan default for sized ones; correction is custom-quote
  'Ceramic Coating': 1700,
  'Avant Guard Formula 1051': 2700,
  'Paint Correction': 0,
  '2-Step Paint Correction': 0,
  'Wheel Coating': 225,
  'Glass Coating': 250,
  'Headlight Restoration': 100,
  // Bare-name lookups for the sized catalog services (Sedan default,
  // matches the constants.js docstring). Without these, staff-side
  // bookings written as just "5Y.2S Coating" / "Signature" / etc.
  // returned $0 from priceForService and the WeekDataPanel +
  // EngineerProfile projected-revenue counts were under by thousands.
  'Signature': 425,
  'Advanced': 425, // legacy alias for pre-rename bookings
  // 'Pro' / 'Pro Service' duplicated lower with the Pro Service block,
  // but kept here too for case-insensitive matching against staff
  // bookings that wrote just "Pro" without a size.
  'Interior or Exterior': 305,
  'Gentleman Spec': 525,
  "Gentleman's Spec": 525,           // canonical (Sedan default)
  'Seasonal Ceramic Coating': 950,   // canonical (Sedan default; +$50 2026-06-11)
  '1Y.1S Coating': 1200,             // retired 2026-06-11; legacy alias for historical bookings only
  '5Y.2S Coating': 1700,
  'AG 1051 Coating': 2700,
  'Seasonal Ceramic Coat (S.C.C.)': 750,
  'PPF Front End': 2600,
  // Strip and Shine
  'Strip and Shine - Ground': 100,
  'Strip and Shine - Lift': 200,
  'Strip and Shine - Wheels Off': 400,
  // Add-ons
  'Clay Bar': 100,
  'Odor Bomb': 85,
  // $150 per occurrence, matching the signed Biohazard Waiver (public/waivers/).
  'Bio Hazard Fee': 150,
  'Specialty Service': 175,
  'Glass Ceramic Coating': 250,
  'Windshield Only': 100,
  'Wheel Faces Ceramic Coated': 225,
  'Wheels Removed - Full Barrel Coating': 400,
  // PPF
  'PPF Front End - Small Vehicle': 2300,
  'PPF Front End - Medium Vehicle': 2600,
  'PPF Front End - Large Vehicle': 2900,

  // ── Winter Menu (season 2026-27) ──────────────────────────────────────
  // The prices the flier advertises, which is what the customer was quoted.
  // Winter Exterior is ALREADY the discounted figure ($235 less 10%), exactly
  // as the website prints it — do not apply a winter discount on top of it.
  'Winter Exterior + Iron Decon': 212,
  'Salt-Stain Reset': 185,
  // Same product as Seasonal Ceramic Coating, at the winter promo: 10% off the
  // $950 Sedan list. Here so a line carrying this name values correctly rather
  // than falling through to nothing; the invoicer catalog is where it is sold.
  'Winter Seasonal Ceramic (winter promo)': 855,
  'Salt-Off Express': 110,
  'Salt-Off Plus': 165,
  // Winter Wash Club — monthly. Recurring dues are collected by the
  // Membership tab (Stripe, or manual billing with these exact service
  // names); these exist so a month billed by hand values correctly.
  'Bronze Winter': 80,
  'Silver Winter': 140,
  'Gold Winter': 250,
  // Storage, per month, by what is being stored (Ryder 2026-09-07). These
  // replaced a single $200 anchor; $150 is now the from-price the website
  // publishes, so the two must move together.
  'Winter Vehicle Storage - Motorcycle': 150,
  'Winter Vehicle Storage - Car': 250,
  'Winter Vehicle Storage - Truck': 300,
  'Winter Vehicle Storage - Boat': 350,
  // 🚨 No RV key on purpose. RVs are quoted per vehicle, so any number here
  // would be a price nobody set — and a wrong price projects into revenue
  // silently, where a missing one just projects nothing.
};
// What is being stored, and the published monthly rate for it — the Storage
// tab and the customer card's "Store" both read this, so the two can't quote
// different numbers. RVs are quoted per vehicle (amount 0 = type it in).
export const STORAGE_KINDS = [
  { kind: 'motorcycle', label: 'Motorcycle', amount: SERVICE_PRICES['Winter Vehicle Storage - Motorcycle'] || 0 },
  { kind: 'car',        label: 'Car',        amount: SERVICE_PRICES['Winter Vehicle Storage - Car'] || 0 },
  { kind: 'truck',      label: 'Truck / SUV', amount: SERVICE_PRICES['Winter Vehicle Storage - Truck'] || 0 },
  { kind: 'boat',       label: 'Boat',       amount: SERVICE_PRICES['Winter Vehicle Storage - Boat'] || 0 },
  { kind: 'rv',         label: 'RV / Camper (quoted)', amount: 0 },
  { kind: 'other',      label: 'Other',      amount: 0 },
];
// Mechanical menu: default hours = top of range; list price = those hours at
// the labor rate. priceForBooking re-prices off the booking's real hours.
Object.entries(MECH_MENU).forEach(([name, m]) => {
  SERVICE_DURATIONS[name] = m.max;
  SERVICE_PRICES[name] = m.max * SHOP_LABOR_RATE;
});

// Case-insensitive lookups so staff casing drift ("ceramic coating",
// "5y.2s coating") still resolves to the canonical map entry. Falls
// back to a `prefix bare` form too — a booking written as just
// "5Y.2S" (no size suffix) maps to the Sedan default for that prefix.
function _lookupBare(key) {
  if (SERVICE_PRICES[key] != null) return SERVICE_PRICES[key];
  const lower = key.toLowerCase();
  for (const k of Object.keys(SERVICE_PRICES)) {
    if (k.toLowerCase() === lower) return SERVICE_PRICES[k];
  }
  return null;
}
function _lookupSized(prefix, size) {
  function _try(pk) {
    const m = SIZED_SERVICE_PRICES[pk]; if (!m) return null;
    if (size != null && m[size] != null) return m[size];
    if (size != null) {
      const sl = size.toLowerCase();
      for (const sk of Object.keys(m)) if (sk.toLowerCase() === sl) return m[sk];
    }
    // No matching size — fall back to Sedan default since that's the
    // documented projection default for staff-booked items.
    return m['Sedan'] != null ? m['Sedan'] : null;
  }
  const direct = _try(prefix);
  if (direct != null) return direct;
  const lower = prefix.toLowerCase();
  for (const pk of Object.keys(SIZED_SERVICE_PRICES)) {
    if (pk.toLowerCase() === lower) {
      const v = _try(pk);
      if (v != null) return v;
    }
  }
  return null;
}

// Staff bookings store bare service names (e.g. "Signature Detail", "Seasonal
// Ceramic Coat (S.C.C.)") with a separate vehicle size — they don't carry the
// " - <size>" suffix customer bookings do. This maps those bare names to the
// SIZED_SERVICE_PRICES prefix so, given the booking's vehicle size, they price
// off the real tier instead of the Sedan default. Mirrors the invoicer CATALOG's
// own name→prefix mapping in public/3c-invoicer.html so projections match what
// the invoicer would quote. Keys are lowercased.
const BARE_TO_SIZED_PREFIX = {
  'interior detail': 'Interior',
  'interior': 'Interior',
  'exterior detail': 'Exterior',
  'exterior': 'Exterior',
  'interior or exterior': 'Int. or Ext.',
  'int. or ext.': 'Int. or Ext.',
  'signature detail': 'Signature',
  'signature': 'Signature',
  'advanced detail': 'Advanced',
  'advanced': 'Advanced',
  'pro service': 'Pro',
  'pro': 'Pro',
  "gentleman's spec": "Gentleman's Spec",
  'gentleman spec': "Gentleman's Spec",
  'seasonal ceramic coat (s.c.c.)': 'Seasonal Ceramic Coating',
  'seasonal ceramic coating': 'Seasonal Ceramic Coating',
  's.c.c.': 'S.C.C.',
  'avant guard formula 1051': 'AG 1051',
  'ag 1051 coating': 'AG 1051',
  'ag 1051': 'AG 1051',
  '5y.2s coating': '5Y.2S',
  '5y.2s': '5Y.2S',
  // OTTO's 5-year coating as a 3COT booking stores it (src/lib/dealers.js).
  '5 year ceramic coating': '5Y.2S',
  '1y.1s coating': '1Y.1S',
  '1y.1s': '1Y.1S',
  // Website booking-modal wording (3cdetailingpro.com BOOKING_SERVICES).
  // Those are customer-facing labels, not catalog keys, so they're aliased
  // here rather than renamed on the site. Mirrors _SIZED_ALIAS in
  // public/3c-invoicer.html — keep the two in step.
  'seasonal coating': 'Seasonal Ceramic Coating',
  '5-year coating': '5Y.2S',
};

// Vehicle-size aliases. The website booking modal labels its tiers for
// customers ("Hatch / Mid SUV"), and that string lands verbatim in
// `bookings.vehicle_size` — matching no key in any sized price map, so the
// booking silently projected at the Sedan fallback. Mirrors _SIZE_ALIAS in
// public/3c-invoicer.html. Unknown values pass through untouched.
const SIZE_ALIAS = {
  'hatch / mid suv':     'Hatchback & Midsize SUV',
  'hatch/mid suv':       'Hatchback & Midsize SUV',
  'full suv / half-ton': 'Full Size SUV & Half Ton Truck',
  'full suv/half-ton':   'Full Size SUV & Half Ton Truck',
  '1-ton / van':         'One Ton Truck & Vans',
  '1-ton/van':           'One Ton Truck & Vans',
};
export function canonVehicleSize(size) {
  const v = String(size || '').trim();
  if (!v) return '';
  const l = v.toLowerCase();
  if (SIZE_ALIAS[l]) return SIZE_ALIAS[l];
  for (const s of VEHICLE_SIZES) if (s.toLowerCase() === l) return s;
  return v;
}

// Booking service strings arrive from several funnels and pick up decoration
// the price maps don't carry — "Signature (Both) ($470)" (website modal,
// pre-2026-08), "Signature (Interior & Exterior)" (website modal, current),
// "Gentleman's Spec ($480)" (quoted-price suffix). Each of those priced at $0.
// Returns the raw string plus progressively-stripped variants to retry with;
// the raw one is always tried FIRST, so a name whose parenthetical is part of
// the real name ("Seasonal Ceramic Coat (S.C.C.)") is unaffected.
function _nameVariants(raw) {
  const out = [raw];
  const noPrice = raw.replace(/\s*\(\s*\$[\d,]+(?:\.\d+)?\s*\)\s*$/, '').trim();
  if (noPrice && !out.includes(noPrice)) out.push(noPrice);
  const bare = noPrice.replace(/\s*\([^()]*\)\s*$/, '').trim();
  if (bare && !out.includes(bare)) out.push(bare);
  return out;
}

// priceForService(name) — flat/Sedan-default lookup (unchanged for existing
// callers). Pass `defaultSize` (a VEHICLE_SIZES value) to price a bare staff
// service at the actual vehicle class — see priceForBooking below.
// One service name, no comma-splitting and no decoration-stripping. Returns
// the price or NULL when nothing in the catalog matched — the null is what
// lets priceForService retry with a stripped variant. A matched-but-free
// service (Paint Correction is quote-work at 0) returns 0, not null, so it
// stops the retry rather than being stripped further.
function _priceOne(s, defaultSize) {
  // Customer-form pattern: "<prefix> - <size>" (ASCII hyphen, en-dash, or
  // em-dash). An explicit size suffix always wins over the booking's size.
  const m = s.match(/^(.+?)\s*[-–—]\s+(.+)$/);
  if (m) {
    const sized = _lookupSized(m[1].trim(), m[2].trim());
    if (sized != null) return sized;
    // Strip and Shine etc. — full-string bare lookup wins over the dash split.
    const wholeBare = _lookupBare(s);
    if (wholeBare != null) return wholeBare;
  }
  // Bare service with a known vehicle size: price it at that size off the sized
  // sheet (via the bare→prefix alias) instead of the flat Sedan default. Only
  // when the service actually lives in the sized catalog; flat add-ons
  // (Extraction, Clay Bar, Wheel Coating, …) fall through to their bare price.
  if (defaultSize) {
    const prefix = BARE_TO_SIZED_PREFIX[s.toLowerCase()] || s;
    const sizedAtVehicle = _lookupSized(prefix, defaultSize);
    if (sizedAtVehicle != null) return sizedAtVehicle;
  }
  const bare = _lookupBare(s);
  if (bare != null) return bare;
  // Last-ditch: bare prefix that matches a sized catalog — common when
  // staff books "5Y.2S Coating" without size suffix and the bare map
  // doesn't have an entry, but the sized prefix does.
  const sizedFromBare = _lookupSized(BARE_TO_SIZED_PREFIX[s.toLowerCase()] || s, defaultSize || null);
  if (sizedFromBare != null) return sizedFromBare;
  return null;
}

export function priceForService(name, defaultSize) {
  const s = String(name || '').trim();
  if (!s) return 0;
  // Multi-service bookings store as "<svc>, <svc>, ..." — sum the parts
  // recursively, carrying the vehicle size down to each part. Without this, any
  // "Interior Detail, Wheel Coating" style booking returned 0 (no map entry for
  // the joined string), which is how the WeekDataPanel projection was off.
  if (s.indexOf(',') !== -1) {
    return s.split(',').reduce((sum, part) => sum + priceForService(part.trim(), defaultSize), 0);
  }
  const size = canonVehicleSize(defaultSize);
  for (const variant of _nameVariants(s)) {
    const p = _priceOne(variant, size);
    if (p != null) return p;
  }
  return 0;
}

// Size-aware price for a booking — the accurate projection input. Uses the
// booking's own vehicle size so a staff booking (bare service, no size suffix)
// prices at its real vehicle class from the invoicer's sized sheet rather than
// defaulting to Sedan. Falls back to the flat price when no size is on file.
export function priceForBooking(b) {
  if (!b) return 0;
  const size = b.vehicleSize || b.vehicle_size || b.size || '';
  // Mechanical is labor: once the booking carries its own hours, price those
  // hours at the shop rate (menu items AND free-text jobs alike), plus any
  // non-mechanical line riding along at its normal price.
  const hrs = Number(b.customDuration != null ? b.customDuration : b.custom_duration) || 0;
  if (hrs > 0 && categoryForBooking(b) === 'mechanical') {
    const parts = String(b.service || '').split(',').map((s) => s.trim()).filter(Boolean);
    const other = parts.filter((p) => !isMechService(p));
    return hrs * SHOP_LABOR_RATE + other.reduce((sum, p) => sum + priceForService(p, size), 0);
  }
  return priceForService(b.service, size);
}

// "Interior or Exterior" / "Int. or Ext." is the ambiguous combined service.
// Customers now pick a side up front, but legacy + in-flight bookings still
// carry the combined label and must be resolved to one or the other before
// they get the right checklist. Used by the approval panel, the Dashboard
// SchedCard, and the Detailer JobCard.
export function isIntOrExtService(svc) {
  return /int\.?\s*or\s*ext|interior\s+or\s+exterior/i.test(String(svc || ''));
}
// Swap the ambiguous "Int. or Ext." prefix for the chosen Interior/Exterior,
// preserving any " - <size>" suffix (e.g. "Int. or Ext. - Sedan" → "Interior - Sedan").
export function resolveIntOrExtService(svc, choice) {
  const label = choice === 'exterior' ? 'Exterior' : 'Interior';
  const s = String(svc || '');
  const replaced = s.replace(/int\.?\s*or\s*ext\.?|interior\s+or\s+exterior/i, label);
  return replaced === s ? label : replaced;
}

// Normalize a raw invoice `service` string into a clean display label for the
// "Revenue/Income by service" breakdowns — or null when the value isn't a real
// service and should be dropped from the chart.
//
// Historical invoices (and any whose PDF service-table parse missed) sometimes
// captured letterhead instead of a line item: the bare word "DETAILING" or the
// legal name "DETAILING 3C OF THE HUDSON VALLEY INC." — neither is a service
// ("detailing" is the whole business, so it's redundant as a line). The parser
// also occasionally glued a stray price fragment onto the name ("AG 1051 – New
// $2"). This strips a trailing $-prefixed price fragment and returns null for
// the letterhead noise so callers exclude it.
// Matches a trailing vehicle-size suffix — " - <size>" / " – <size>" with any
// of the known VEHICLE_SIZES (longest first so multi-word sizes win). Built
// once at module load. Used to collapse "Pro – Full Size SUV & Half Ton Truck"
// → "Pro" so the breakdown groups by service TYPE, not by vehicle size.
const _SIZE_SUFFIX_RE = new RegExp(
  '\\s*[-–—]\\s*(' +
  VEHICLE_SIZES.slice().sort((a, b) => b.length - a.length)
    .map((s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|') +
  ')\\s*$',
  'i'
);

export function serviceLabel(raw) {
  // Match the existing breakdowns: take the first comma-separated segment.
  let s = String(raw || '').split(',')[0].trim();
  // Strip a trailing "$N"/"$N.NN" price fragment the parser sometimes appended.
  // Require the leading "$" so we never truncate a real name that ends in a
  // number (e.g. the coating service "AG 1051").
  s = s.replace(/\s+\$-?[\d,]+(\.\d+)?$/, '').trim();
  // Drop the vehicle-size suffix so sized variants aggregate into one service
  // type ("Pro – Coupe" + "Pro – Sedan" → "Pro"). Size doesn't matter for the
  // revenue-by-service view; the bigger combined total is the point.
  s = s.replace(_SIZE_SUFFIX_RE, '').trim();
  if (!s) return null;
  const u = s.toUpperCase();
  if (u === 'DETAILING') return null;            // bare generic header — redundant
  if (u.includes('HUDSON VALLEY')) return null;  // business legal name as letterhead
  if (/^DETAILING\s+3C\b/.test(u)) return null;  // any "DETAILING 3C …" letterhead variant
  return s;
}

// ---------------------------------------------------------------------------
// The bookable service catalogue — the list a human picks from.
// ---------------------------------------------------------------------------
// Split by bay, because that is how the booking form groups them and how the
// scheduler reasons about them. Lifted out of Booking.jsx (2026-09-08) when the
// TAR upsell composer needed the same list: two hand-maintained copies of "what
// we sell" is exactly the kind of pair that drifts, and a service missing from
// one of them is a service nobody can be offered.
//
// ⚠️ These are the labels a customer sees AND the keys priceForService() looks
// up. Renaming an entry without adding the old name to SERVICE_PRICES /
// SIZED_SERVICE_PRICES as an alias makes it price at $0.
export const WASH_SERVICES = ['Interior Detail','Exterior Detail','Signature Detail',"Gentleman's Spec",'Extraction','Extraction +','Trim & Plastic Restoration','Strip and Shine - Ground','Strip and Shine - Lift','Strip and Shine - Wheels Off','Winter Exterior + Iron Decon','Salt-Stain Reset','Salt-Off Express','Salt-Off Plus','Backseat Overhaul','New Parent Prep'];
export const CLEAN_SERVICES = ['Ceramic Coating','Seasonal Ceramic Coat (S.C.C.)','Avant Guard Formula 1051','Paint Correction','2-Step Paint Correction','Wheel Coating','Glass Coating','Headlight Restoration'];

// The catalogue for one category. The booking page's category ticker reads this
// to decide which service buttons to show, and to drop selections that belong
// to a category you just switched away from.
export const CATEGORY_SERVICES = {
  general:    WASH_SERVICES,
  specialty:  CLEAN_SERVICES,
  mechanical: MECH_SERVICES,
};

// Which category a service NAME belongs to. Used when something other than the
// ticker chooses the service first (a waitlist claim prefills one), so the
// ticker lands on the category that service actually lives in instead of
// hiding it. Unknown names — every free-text entry — read as general.
export function categoryForService(name) {
  if (CLEAN_SERVICES.includes(name)) return 'specialty';
  if (MECH_SERVICES.includes(name)) return 'mechanical';
  return 'general';
}

// Flat catalogue for pickers that don't care which bay a service runs in.
// MECH_SERVICES is empty today (mechanical work is free-text only), so this is
// unchanged in practice — spreading it here means a mechanical menu added later
// reaches every one of those pickers without hunting them down.
export const SERVICE_OPTIONS = [...WASH_SERVICES, ...CLEAN_SERVICES, ...MECH_SERVICES];

// ── Membership plan → the service the member is really buying ───────────────
// A member card says "Silver" but the bay works in service names, so the
// Membership tab shows the equivalent next to the plan. Matching is by tier
// word so "Silver", "Silver 12-Month", "silver winter" all resolve; the Winter
// Wash Club and Backseat Overhaul plans have their own products. Returns
// { service, note } or null for a plan 3CHA doesn't recognise.
export function membershipServiceEquiv(plan) {
  const p = String(plan || '').toLowerCase();
  if (!p) return null;
  if (p.includes('backseat')) return { service: 'Backseat Overhaul', note: 'once a month' };
  const winter = p.includes('winter');
  if (p.includes('gold'))   return winter ? { service: 'Salt-Off Plus',    note: '2 × per month' } : { service: "Gentleman's Spec", note: 'full interior + exterior, leather conditioning, engine bay — every month' };
  if (p.includes('silver')) return winter ? { service: 'Salt-Off Express', note: '2 × per month' } : { service: 'Signature Detail', note: 'full interior + exterior — every month' };
  if (p.includes('bronze')) return winter ? { service: 'Salt-Off Express', note: '1 × per month' } : { service: 'Exterior Detail',  note: 'express exterior + interior vacuum & wipe-down — every month' };
  return null;
}
